import { useEffect, useState } from "react";
import { useGlobalContext } from "../context/GlobalContext";
import { BorderBox, Footer, FinalNotes } from "./";

export default function Content({
  contentRef,
  getSelectColor,
  lessonContent,
  highlightColor,
  fontSize,
  onHighlight,
}) {
  const [footerElement, setFooterElement] = useState(null);
  const { darkMode } = useGlobalContext();

  useEffect(() => {
    if (contentRef.current) {
      const darkModeOnlyElements =
        contentRef.current.querySelectorAll(".dark-mode-only");
      const lightModeOnlyElements =
        contentRef.current.querySelectorAll(".light-mode-only");

      darkModeOnlyElements.forEach((element) => {
        element.style.display = darkMode ? "" : "none";
      });

      lightModeOnlyElements.forEach((element) => {
        element.style.display = darkMode ? "none" : "";
      });
    }
  // contentRef is a ref object - its .current value changes but the ref itself
  // is stable. ESLint flags it but it does not need to be in the dep array.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [darkMode]);

  useEffect(() => {
    if (contentRef.current) {
      // Find the parent BorderBox which is the scrollable container
      const scrollableParent = contentRef.current.closest(".overflow-y-auto");
      if (scrollableParent) {
        scrollableParent.scrollTop = 0;
      }

      setTimeout(() => {
        const foundFooter = contentRef.current.querySelector("footer");
        setFooterElement(foundFooter);
      }, 0);
    }

    return () => {
      setFooterElement(null);
    };
  }, [lessonContent, contentRef]);

  const isSelectionWithinContent = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return false;

    const container = contentRef.current;
    const range = selection.getRangeAt(0);
    return container && container.contains(range.commonAncestorContainer);
  };

  const expandSelectionToWords = (selection) => {
    if (!selection.rangeCount) return null;
    const range = selection.getRangeAt(0);
    const start = range.startContainer;
    const end = range.endContainer;

    // Only proceed if we're dealing with text nodes
    if (start.nodeType !== Node.TEXT_NODE || end.nodeType !== Node.TEXT_NODE)
      return null;

    // Check if either node's parent element is inside a table
    if (
      start.parentElement?.closest("table") ||
      end.parentElement?.closest("table")
    ) {
      return null;
    }

    const startText = start.textContent;
    const endText = end.textContent;

    // Find word boundaries
    let startOffset = range.startOffset;
    while (startOffset > 0 && /\S/.test(startText[startOffset - 1])) {
      startOffset--;
    }

    let endOffset = range.endOffset;
    while (endOffset < endText.length && /\S/.test(endText[endOffset])) {
      endOffset++;
    }

    // Create new range with word boundaries
    const newRange = document.createRange();
    newRange.setStart(start, startOffset);
    newRange.setEnd(end, endOffset);

    // Check if the expanded range crosses any table boundaries
    const walker = document.createTreeWalker(
      newRange.commonAncestorContainer,
      NodeFilter.SHOW_ELEMENT,
      null,
      false
    );

    let currentNode;
    let foundTable = false;

    while ((currentNode = walker.nextNode())) {
      if (currentNode.tagName === "TABLE") {
        // Check if the range intersects with this table
        const tableRange = document.createRange();
        tableRange.selectNodeContents(currentNode);

        if (
          newRange.compareBoundaryPoints(Range.END_TO_START, tableRange) > 0 &&
          newRange.compareBoundaryPoints(Range.START_TO_END, tableRange) < 0
        ) {
          foundTable = true;
          break;
        }
      }
    }

    if (foundTable) {
      return null;
    }

    return newRange;
  };

  const handleSelection = () => {
    const selection = window.getSelection();
    const selectionText = selection.toString().trim();

    if (selectionText && isSelectionWithinContent()) {
      const expandedRange = expandSelectionToWords(selection);
      if (expandedRange) {
        // Create a new selection with the expanded range
        const newSelection = window.getSelection();
        newSelection.removeAllRanges();
        newSelection.addRange(expandedRange);

        // Pass both the selection and expanded range
        onHighlight(newSelection, expandedRange);
      }
    }
  };

  useEffect(() => {
    document.addEventListener("mouseup", handleSelection);
    return () => {
      document.removeEventListener("mouseup", handleSelection);
    };
  // handleSelection is recreated on every render (plain function). The effect
  // intentionally re-registers only when highlightColor changes so the latest
  // color is captured via onHighlight. contentRef is a stable ref object.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightColor]);

  if (!lessonContent) {
    return null;
  }

  // Locked lesson: render the backend-provided body as usual, but make the page
  // inert and stripped-down. We keep `id="content"` so the lesson body styling
  // scoped to `#content` in index.css (headings, links, tables, etc.) still
  // applies, but intentionally omit `ref={contentRef}` so every highlight +
  // annotation effect in Lesson.jsx (all guarded on `contentRef.current`) no-ops.
  // `select-none` blocks text selection for highlighting. FinalNotes (notes
  // editor + "Continue" CTA) and the hardcoded legal Footer are both omitted.
  if (lessonContent.is_locked) {
    return (
      <BorderBox overflow>
        <div
          id="content"
          className={`text-lz-ink-mid dark:text-lz-concrete p-0 md:p-4 select-none ${
            fontSize === "sm"
              ? "text-lg/10"
              : fontSize === "lg"
              ? "text-2xl/10"
              : "text-xl/10"
          }`}
        >
          <div dangerouslySetInnerHTML={{ __html: lessonContent.body }} />
        </div>
      </BorderBox>
    );
  }

  return (
    <BorderBox overflow>
      <div
        id="content"
        ref={contentRef}
        className={`text-lz-ink-mid dark:text-lz-concrete p-0 md:p-4 ${
          fontSize === "sm"
            ? "text-lg/10"
            : fontSize === "lg"
            ? "text-2xl/10"
            : "text-xl/10"
        } selection:text-black ${getSelectColor(highlightColor)}`}
      >
        <div dangerouslySetInnerHTML={{ __html: lessonContent.body }} />
      </div>

      <div id="final-notes-anchor">
        <FinalNotes
          lessonContent={lessonContent}
          showNotes={lessonContent.show_final_notes}
        />
      </div>

      {footerElement && <Footer />}
    </BorderBox>
  );
}
